import React from "react";
import { Box, Camera, GitMerge, Grid3X3, MapPin, Spline, Zap } from "lucide-react";
import {
  EllipsoidIcon,
  EntireObjectIcon,
  ExplosionIcon,
  JointIcon,
  VisualMeshIcon,
} from "../../ui/Icons";
import {
  setSelectedVisualMeshIds,
  useSelectedVisualMeshIds,
} from "../../../visual-mesh-multi-selection";
import {
  buildActuatorTargets,
  buildBodyTargets,
  buildCameraTargets,
  buildEqualityTargets,
  buildGeomTargets,
  buildInertialTargets,
  buildJointTargets,
  buildMeshTargets,
  buildObjectTargets,
  buildRecordTargets,
  buildSceneOrder,
} from "./sceneInventoryTargets";

export function useSceneInventoryRows({
  actuators,
  bodies,
  cameras,
  equalities,
  joints,
  meshes,
  sites,
  tendons,
  worldBodies,
}) {
  const selectedMeshIds = useSelectedVisualMeshIds();
  const sceneOrder = React.useMemo(
    () => buildSceneOrder(bodies, worldBodies, tendons),
    [bodies, worldBodies, tendons]
  );

  const bodyTargets = React.useMemo(
    () => buildBodyTargets(bodies, sceneOrder),
    [bodies, sceneOrder]
  );

  const objectTargets = React.useMemo(
    () => buildObjectTargets(bodies, worldBodies, sceneOrder),
    [bodies, sceneOrder, worldBodies]
  );

  const collisionTargets = React.useMemo(
    () => buildGeomTargets(bodies, sceneOrder, false),
    [bodies, sceneOrder]
  );

  const visualTargets = React.useMemo(
    () => buildGeomTargets(bodies, sceneOrder, true),
    [bodies, sceneOrder]
  );

  const inertialTargets = React.useMemo(
    () => buildInertialTargets(bodies, sceneOrder),
    [bodies, sceneOrder]
  );

  const meshTargets = React.useMemo(
    () => buildMeshTargets(bodies, meshes, sceneOrder),
    [bodies, meshes, sceneOrder]
  );

  React.useEffect(() => {
    const availableMeshIds = new Set(meshTargets.map((target) => target.id));
    const next = new Set([...selectedMeshIds].filter((meshUuid) => availableMeshIds.has(meshUuid)));
    if (next.size !== selectedMeshIds.size) {
      setSelectedVisualMeshIds(next);
    }
  }, [meshTargets, selectedMeshIds]);

  const recordTargets = React.useCallback(
    (record, fallbackLabel) => buildRecordTargets(record, fallbackLabel, sceneOrder),
    [sceneOrder]
  );

  const jointTargets = React.useMemo(
    () => buildJointTargets(joints, sceneOrder),
    [joints, sceneOrder]
  );
  const siteTargets = React.useMemo(() => recordTargets(sites, "Site"), [sites, recordTargets]);
  const actuatorTargets = React.useMemo(
    () => buildActuatorTargets(actuators, sceneOrder),
    [actuators, sceneOrder]
  );
  const equalityTargets = React.useMemo(
    () => buildEqualityTargets(equalities, sceneOrder),
    [equalities, sceneOrder]
  );
  const tendonTargets = React.useMemo(
    () => recordTargets(tendons, "Tendon"),
    [recordTargets, tendons]
  );
  const cameraTargets = React.useMemo(
    () => buildCameraTargets(cameras, sceneOrder),
    [cameras, sceneOrder]
  );

  const rows = React.useMemo(
    () => [
      {
        key: "object",
        label: "Object",
        graphType: "body",
        Icon: EntireObjectIcon,
        iconCls: "w-2.5 h-2.5",
        targets: objectTargets,
        hasVisibility: true,
      },
      {
        key: "body",
        label: "Body",
        graphType: "body",
        Icon: Box,
        iconCls: "w-2.5 h-2.5",
        targets: bodyTargets,
        hasVisibility: true,
        supportsMultiSelect: true,
      },
      {
        key: "joint",
        label: "Joint",
        graphType: "joint",
        Icon: JointIcon,
        iconCls: "w-2 h-2",
        targets: jointTargets,
        hasVisibility: true,
      },
      {
        key: "actuator",
        label: "Actuator",
        graphType: "actuator",
        Icon: Zap,
        iconCls: "w-2 h-2",
        targets: actuatorTargets,
        hasVisibility: false,
      },
      {
        key: "mesh",
        label: "Visual: Mesh",
        graphType: "mesh",
        Icon: Grid3X3,
        iconCls: "w-2 h-2",
        targets: meshTargets,
        hasVisibility: true,
        supportsMultiSelect: true,
      },
      {
        key: "visual",
        label: "Visual: Geometry",
        graphType: "mesh",
        Icon: VisualMeshIcon,
        iconCls: "w-2.5 h-2.5",
        targets: visualTargets,
        hasVisibility: true,
      },
      {
        key: "collision",
        label: "Collision",
        graphType: "geom",
        Icon: ExplosionIcon,
        iconCls: "w-2 h-2",
        targets: collisionTargets,
        hasVisibility: true,
      },
      {
        key: "site",
        label: "Site",
        graphType: "site",
        Icon: MapPin,
        iconCls: "w-2 h-2",
        targets: siteTargets,
        hasVisibility: true,
      },
      {
        key: "inertial",
        label: "Inertial",
        graphType: "inertial",
        Icon: EllipsoidIcon,
        iconCls: "w-2 h-2",
        targets: inertialTargets,
        hasVisibility: true,
      },
      {
        key: "constraint",
        label: "Constraint",
        graphType: "equality",
        Icon: GitMerge,
        iconCls: "w-2 h-2",
        targets: equalityTargets,
        hasVisibility: true,
      },
      {
        key: "tendon",
        label: "Tendon",
        graphType: "tendon",
        Icon: Spline,
        iconCls: "w-2 h-2",
        targets: tendonTargets,
        hasVisibility: true,
      },
      {
        key: "camera",
        label: "Camera",
        graphType: "camera",
        Icon: Camera,
        iconCls: "w-2 h-2",
        targets: cameraTargets,
        hasVisibility: true,
      },
    ],
    [
      actuatorTargets,
      bodyTargets,
      cameraTargets,
      collisionTargets,
      equalityTargets,
      inertialTargets,
      jointTargets,
      meshTargets,
      objectTargets,
      siteTargets,
      tendonTargets,
      visualTargets,
    ]
  );

  return {
    rows,
    sceneOrder,
    selectedMeshCount: selectedMeshIds.size,
    selectedMeshIds,
  };
}
